// Shared logic behind every referral call-to-action surface.
//
// The dashboard banner, the home ribbon, and the upsell dialog all need the same
// four things: whether the visitor is a guest, their referral code once it loads,
// a copy-to-clipboard action, and a share action that prefers the native share
// sheet. Only the presentation differs between the three, so the logic lives here
// and each surface styles its own buttons.
//
// Navigation is deliberately NOT part of this hook — the banner uses a real anchor
// to /signup, the ribbon and dialog navigate programmatically.

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { getCachedRewards, REWARDS_TTL_MS } from "../utils/accountCache";
import { writeToClipboard } from "../utils/clipboard";

const COPIED_FLASH_MS = 2000;

export type ReferralCta = {
  /** True once auth has resolved and nobody is signed in. */
  isGuest: boolean;
  /**
   * True while auth is still resolving. Surfaces should hold their space rather
   * than guess, so a returning member never sees a "Register now" flash.
   */
  loading: boolean;
  referralCode: string | null;
  /** Empty string until the code loads. */
  shareLink: string;
  /** True for a couple of seconds after a successful copy. */
  copied: boolean;
  /**
   * True for a couple of seconds after a copy attempt fails (permission denied,
   * clipboard unavailable, etc.) so the surface can tell the user instead of
   * silently doing nothing.
   */
  copyFailed: boolean;
  copyCode: () => void;
  shareInvite: () => Promise<void>;
};

export function useReferralCta(): ReferralCta {
  const { user, loading } = useAuth();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const isGuest = !loading && !user;

  useEffect(() => {
    if (!user?.firebase_id) return;
    getCachedRewards(user.firebase_id, REWARDS_TTL_MS).then((result) => {
      if (result.data?.referral?.referralCode) {
        setReferralCode(result.data.referral.referralCode);
      }
    });
  }, [user?.firebase_id]);

  const shareLink = referralCode
    ? `${window.location.origin}/ref/${referralCode}`
    : "";

  const flashCopied = useCallback(() => {
    setCopyFailed(false);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FLASH_MS);
  }, []);

  const flashCopyFailed = useCallback(() => {
    setCopied(false);
    setCopyFailed(true);
    setTimeout(() => setCopyFailed(false), COPIED_FLASH_MS);
  }, []);

  const copyCode = useCallback(() => {
    if (!referralCode) return;
    void writeToClipboard(referralCode).then((ok) => (ok ? flashCopied() : flashCopyFailed()));
  }, [referralCode, flashCopied, flashCopyFailed]);

  const shareInvite = useCallback(async () => {
    if (!shareLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on HomeHelpy",
          text: "Sign up with my link and we both get a free hour of cleaning!",
          url: shareLink,
        });
        return;
      } catch {
        // User dismissed the native share sheet, so fall back to copying the link.
      }
    }
    const ok = await writeToClipboard(shareLink);
    if (ok) flashCopied();
    else flashCopyFailed();
  }, [shareLink, flashCopied, flashCopyFailed]);

  return {
    isGuest,
    loading,
    referralCode,
    shareLink,
    copied,
    copyFailed,
    copyCode,
    shareInvite,
  };
}
